import React from 'react';
import {
  Box,
  VStack,
  Text,
  Heading,
  Button,
  Container,
  Link,
  SimpleGrid,
  Card,
  AspectRatio
} from '@chakra-ui/react';

import { FaExternalLinkAlt, FaYoutube } from 'react-icons/fa';
import { SOCIAL_MEDIA_LINKS } from './core/constants';
import { COLORS } from '../theme';
import { FONTS } from '../constants/fonts';
import { useColorModeValue } from './chakra-ui/color-mode';

import { Skeleton, SkeletonText } from './ui/skeleton';

function VideoSection({ allVideos }) {
  const cardBg = useColorModeValue(COLORS.glass.bg.light, COLORS.glass.bg.dark);
  const cardBorder = useColorModeValue(
    COLORS.glass.border.light,
    COLORS.glass.border.dark
  );
  const cardShadow = useColorModeValue(
    COLORS.glass.shadow.light,
    COLORS.glass.shadow.dark
  );
  const descColor = useColorModeValue('gray.600', 'gray.400');

  const isDataLoading = !allVideos || allVideos.length === 0;
  const itemsToDisplay = isDataLoading ? [1, 2, 3] : allVideos;

  return (
    <Box>
      <Container maxW="container.xl">
        <VStack spacing={12}>
          <VStack spacing={4} textAlign="center">
            <Heading size="2xl">Video Showcase</Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              Explore our video gallery and experience our photography in
              motion.
            </Text>
          </VStack>

          <SimpleGrid
            columns={{ base: 1, lg: 2, xl: 3 }}
            spacing={8}
            w="full"
            gap={4}
          >
            {itemsToDisplay.map((video, index) =>
              isDataLoading ? (
                <Card.Root
                  key={index}
                  bg={cardBg}
                  border="1px solid"
                  borderColor={cardBorder}
                  borderRadius="2xl"
                  overflow="hidden"
                >
                  <AspectRatio ratio={16 / 9}>
                    <Skeleton h="full" w="full" />
                  </AspectRatio>
                  <Box p={6}>
                    <SkeletonText noOfLines={2} gap="4" />
                  </Box>
                </Card.Root>
              ) : (
                <Card.Root
                  key={video.youtubeId}
                  bg={cardBg}
                  backdropFilter={`blur(${COLORS.glass.blur})`}
                  border="1px solid"
                  borderColor={cardBorder}
                  borderRadius="2xl"
                  shadow={cardShadow}
                  overflow="hidden"
                  transition="all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)"
                  _hover={{
                    transform: 'translateY(-8px)',
                    shadow: '2xl',
                    borderColor: COLORS.brand.primary
                  }}
                >
                  <Card.Body gap="0" p={0}>
                    <AspectRatio ratio={16 / 9} mb={0}>
                      <iframe
                        src={`https://www.youtube.com/embed/${video.youtubeId}`}
                        title={video.title || `YouTube video ${index + 1}`}
                        allowFullScreen
                        style={{ borderBottom: `1px solid ${cardBorder}` }}
                      />
                    </AspectRatio>
                    <Box p={6}>
                      <Card.Title
                        mb="2"
                        fontSize="xl"
                        fontFamily={FONTS.heading}
                      >
                        {video.title}
                      </Card.Title>
                      <Card.Description
                        fontSize="md"
                        color={descColor}
                        lineHeight="tall"
                      >
                        {video.description}
                      </Card.Description>
                    </Box>
                  </Card.Body>
                </Card.Root>
              )
            )}
          </SimpleGrid>

          <Button
            mt={4}
            colorScheme="red"
            variant="outline"
            size="lg"
            as={Link}
            href={SOCIAL_MEDIA_LINKS.youtube}
            target="_blank"
            rel="noopener noreferrer"
            minH="48px"
            _hover={{
              bg: 'red.50',
              transform: 'translateY(-2px)',
              shadow: 'lg'
            }}
          >
            <FaYoutube /> Visit Our YouTube Channel <FaExternalLinkAlt />
          </Button>
        </VStack>
      </Container>
    </Box>
  );
}

export default VideoSection;
